import {
  Box,
  Flex,
  Text,
  Button,
  Image,
  Link,
  UnorderedList,
  ListItem,
  Grid,
  Divider,
  Center,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import checkCircleSrc from '../../../assets/images/check-circle.svg';
import { WarningIcon, LockIcon } from '@chakra-ui/icons';
import GradientBorderCard from '../../../common/GradientBorderCard';
import GoLiveModal from './GoLiveModal';
import {
  PLAN_FEATURES_CONFIG,
  findFeatureByKey,
  formatFeatureValue,
  hasFeatureValue,
} from './planFeaturesConfig';
import { paymentMethodLabel } from '../../../utils/paymentMethodLabel';
import {
  billingFrequencyAdverb,
  billingPeriodNoun,
  unlockedFirst,
  columnMajorGridProps,
  newColumnStartProps,
  formatCustomPlanName,
} from '../../../utils/subscriptionUtils';
import { MeContext } from '../../../context/MeContext';

const PlanAndSubscription = ({
  subscription,
  trial,
  paymentMethod,
  isLoading,
  daysRemainingInTrial,
  onTrial,
  onOpenCancelTrialModal,
  onOpenSubscriptionComparisonModal,
  formatPrice,
  onOpenResubscribeModal,
  onOpenCardlessTrialConversionModal,
}) => {
  const goLiveModal = useDisclosure();
  const { state } = useContext(MeContext);
  const watermarkFree = state?.watermarkFree;
  const planFeatures = watermarkFree
    ? PLAN_FEATURES_CONFIG.filter(
        (config) => config.key !== 'passentry_watermark'
      )
    : PLAN_FEATURES_CONFIG;

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('openGoLive') !== '1') return;
    if (isLoading) return;

    if (subscription?.status === 'canceled') {
      onOpenResubscribeModal?.();
    } else if (onTrial) {
      goLiveModal.onOpen();
    } else if (subscription?.status === 'active') {
      onOpenSubscriptionComparisonModal?.();
    } else if (!subscription) {
      onOpenCardlessTrialConversionModal?.();
    }

    const next = new URLSearchParams(searchParams);
    next.delete('openGoLive');
    setSearchParams(next, { replace: true });
  }, [searchParams, isLoading, subscription, onTrial]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  const features = subscription?.plan?.features || trial?.features;

  const planFeaturesWithStatus = planFeatures.map((config) => {
    const feature = findFeatureByKey(features, config.key);
    return { config, feature, included: hasFeatureValue(feature, config) };
  });
  const orderedPlanFeatures = unlockedFirst(
    planFeaturesWithStatus,
    (f) => f.included
  );
  const firstLockedFeatureIndex = orderedPlanFeatures.findIndex(
    (f) => !f.included
  );

  return (
    <>
      <Box bg="white" borderRadius="15px" borderWidth="1px" p={8}>
        <Flex
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text textStyle="bodyFamilyRegular" fontSize="14px" color="#000000">
            Current plan
          </Text>
          <Button
            variant="gradient"
            display={{
              base: 'block',
              md: 'none',
            }}
            size="sm"
            px={3}
            onClick={() => {
              onOpenSubscriptionComparisonModal?.();
            }}
          >
            <Text textStyle="bodyFamilyBold" fontSize="14px" color="white">
              Upgrade
            </Text>
          </Button>
        </Flex>
        <Flex
          direction={{
            base: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
        >
          <Flex
            direction="row"
            gap="15px"
            alignItems="center"
            mt="10px"
            mb={{ base: '0', md: '10px' }}
          >
            <Text fontFamily="Gilroy Medium" fontSize="32px" color="#000000">
              {subscription
                ? formatCustomPlanName(subscription?.plan?.name)
                : 'Free Trial'}
            </Text>
            {daysRemainingInTrial > 0 && (
              <Text textStyle="bodyFamilyRegular" fontSize="14px" color="brand">
                {daysRemainingInTrial} days left
              </Text>
            )}
          </Flex>
          <Flex gap={3} display={{ base: 'none', md: 'flex' }}>
            {onTrial && (
              <Button
                variant="secondary"
                onClick={() => {
                  onOpenSubscriptionComparisonModal?.();
                }}
              >
                <Text>Upgrade Plan</Text>
              </Button>
            )}
            <Button
              variant="gradient"
              onClick={() => {
                if (subscription?.status === 'canceled') {
                  onOpenResubscribeModal?.();
                } else if (onTrial) {
                  goLiveModal.onOpen();
                } else if (subscription?.status === 'active') {
                  onOpenSubscriptionComparisonModal?.();
                } else if (!subscription) {
                  onOpenCardlessTrialConversionModal?.();
                }
              }}
            >
              <Text textStyle="bodyFamilyBold" fontSize="14px" color="white">
                {subscription?.status === 'canceled'
                  ? 'Resubscribe'
                  : subscription?.status === 'active'
                    ? 'Upgrade Plan'
                    : !subscription
                      ? 'Select a plan'
                      : 'Go Live'}
              </Text>
            </Button>
          </Flex>
        </Flex>
        <Divider mt="10px" border="1px solid lightGrey" />
        <Text textStyle="bodyFamilySemiBold" fontSize="14px" mt="30px">
          Plan features:
        </Text>
        <UnorderedList spacing={3} mt={4} ml={0} mb={3}>
          <Grid
            {...columnMajorGridProps(orderedPlanFeatures.length, {
              base: 1,
              lg: 3,
            })}
            gap={3}
          >
            {orderedPlanFeatures.map(({ config, feature, included }, index) => {
              return (
                <ListItem
                  key={config.key}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  {...(index === firstLockedFeatureIndex
                    ? newColumnStartProps({ base: 1, lg: 3 })
                    : {})}
                >
                  {included ? (
                    <Image src={checkCircleSrc} alt="check" w="20px" h="20px" />
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      w="20px"
                      h="20px"
                      flexShrink={0}
                    >
                      <LockIcon boxSize={4} color="grey" />
                    </Flex>
                  )}
                  <Text
                    textStyle="headingFamilyMedium"
                    fontSize="14px"
                    color={included ? '#101828' : 'grey'}
                  >
                    {formatFeatureValue(feature, config)}
                  </Text>
                </ListItem>
              );
            })}
          </Grid>
        </UnorderedList>
        {subscription && (
          <Flex direction="row" align="center" mt="35px" gap={1}>
            <WarningIcon color="darkGray" w="14px" h="14px" />
            {onTrial ? (
              <Text textStyle="bodyFamilyRegular" fontSize="14px">
                Your subscription will go live on{' '}
                {moment(subscription?.trialEndsAt).format('DD/MM/YYYY')}, and
                you will be billed{' '}
                {billingFrequencyAdverb(
                  subscription?.plan?.interval,
                  subscription?.plan?.intervalCount
                )}
                .
              </Text>
            ) : subscription?.status === 'canceled' ? (
              <Text textStyle="bodyFamilyRegular" fontSize="14px">
                Your subscription has been cancelled.
              </Text>
            ) : (
              <Text textStyle="bodyFamilyRegular" fontSize="14px">
                You will be billed{' '}
                {billingFrequencyAdverb(
                  subscription?.plan?.interval,
                  subscription?.plan?.intervalCount
                )}
                .
              </Text>
            )}
          </Flex>
        )}
      </Box>
      <Grid
        templateColumns={{
          base: '1fr',
          lg: '1fr 2fr',
        }}
        gap={{
          base: 6,
          lg: 4,
        }}
        w="100%"
      >
        {subscription && (
          <>
            <GradientBorderCard>
              <Text textStyle="heading3">Next payment</Text>
              <Divider
                mt="5px"
                border="1px solid"
                borderColor="orangePrimary"
                opacity={0.6}
              />
              <Text textStyle="bodyFamilyRegular" fontSize="14px" mt="20px">
                {onTrial ? 'Trial ends:' : 'Next payment date:'}
              </Text>
              {subscription?.status === 'canceled' ? (
                <Text textStyle="heading4" mt="5px">
                  -
                </Text>
              ) : onTrial ? (
                <Text textStyle="heading4" mt="5px">
                  {moment(subscription?.trialEndsAt).format('DD MMMM YYYY')}
                </Text>
              ) : (
                <Text textStyle="heading4" mt="5px">
                  {moment(subscription?.nextBillingDate).format('DD MMMM YYYY')}
                </Text>
              )}
              <Text textStyle="bodyFamilyRegular" fontSize="14px" mt="10px">
                Amount due:
              </Text>
              <Text textStyle="heading4" mt="5px">
                {subscription?.status === 'canceled' ? (
                  '-'
                ) : (
                  <Text textStyle="heading4">
                    {formatPrice(subscription?.plan?.amount)}{' '}
                    {
                      <Text
                        as="span"
                        textStyle="bodyFamilyRegular"
                        fontSize="18px"
                      >
                        /
                        {billingPeriodNoun(
                          subscription?.plan?.interval,
                          subscription?.plan?.intervalCount
                        )}
                      </Text>
                    }
                  </Text>
                )}
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="14px" mt="10px">
                Payment method:
              </Text>
              <Text textStyle="heading4" mt="5px">
                {subscription?.status === 'canceled'
                  ? '-'
                  : paymentMethodLabel(paymentMethod)}
              </Text>
            </GradientBorderCard>
            <Box bg="white" borderRadius="15px" borderWidth="1px" p={8}>
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="14px"
                color="orangePrimary"
              >
                Plan details
              </Text>
              <Text textStyle="heading3" mt="10px">
                Subscription
              </Text>
              <Divider mt="5px" border="1px solid lightGrey" />
              <Text textStyle="bodyFamilySemiBold" fontSize="14px" mt="30px">
                {onTrial ? 'Trial ends:' : 'Next payment date:'}{' '}
                {subscription?.status === 'canceled' ? (
                  <Text textStyle="bodyFamilyRegular" as="span">
                    -
                  </Text>
                ) : onTrial ? (
                  <Text textStyle="bodyFamilyRegular" as="span">
                    {moment(subscription?.trialEndsAt).format('DD MMMM YYYY')}
                  </Text>
                ) : (
                  <Text textStyle="bodyFamilyRegular" as="span">
                    {moment(subscription?.nextBillingDate).format(
                      'DD MMMM YYYY'
                    )}
                  </Text>
                )}
              </Text>
              {subscription?.status === 'active' && (
                <Text textStyle="bodyFamilySemiBold" fontSize="14px" mt="15px">
                  Go live date:{' '}
                  <Text textStyle="bodyFamilyRegular" as="span">
                    {moment(subscription?.liveAt).format('DD MMMM YYYY')}
                  </Text>
                </Text>
              )}
              <Text textStyle="bodyFamilySemiBold" fontSize="14px" mt="15px">
                Add on bundles:{' '}
                <Text textStyle="bodyFamilyRegular" as="span">
                  Coming soon
                </Text>
              </Text>
              {onTrial ? (
                <>
                  <Text
                    textStyle="bodyFamilyRegular"
                    color="secondaryDark"
                    fontSize="14px"
                    mt="30px"
                  >
                    Thinking of leaving?{' '}
                    <Link
                      onClick={() => onOpenCancelTrialModal?.()}
                      textStyle="bodyFamilyRegular"
                      fontSize="14px"
                      textDecoration="underline"
                      _hover={{
                        color: 'orangePrimary',
                      }}
                    >
                      Cancel here
                    </Link>{' '}
                  </Text>
                  <Text
                    textStyle="bodyFamilyRegular"
                    color="secondaryDark"
                    fontSize="14px"
                    mt="10px"
                  >
                    Or contact{' '}
                    <Link
                      href="mailto:sales@passentry.com"
                      textStyle="bodyFamilyRegular"
                      fontSize="14px"
                      textDecoration="underline"
                      _hover={{
                        color: 'orangePrimary',
                      }}
                    >
                      sales@passentry.com
                    </Link>{' '}
                    to discuss a custom plan
                  </Text>
                </>
              ) : (
                <Text
                  textStyle="bodyFamilyRegular"
                  color="secondaryDark"
                  fontSize="14px"
                  mt="30px"
                >
                  Thinking of leaving? Contact us at{' '}
                  <Link
                    href="mailto:sales@passentry.com"
                    textStyle="bodyFamilyRegular"
                    fontSize="14px"
                    textDecoration="underline"
                    _hover={{
                      color: 'orangePrimary',
                    }}
                  >
                    sales@passentry.com
                  </Link>
                </Text>
              )}
            </Box>
          </>
        )}
      </Grid>
      <GoLiveModal isOpen={goLiveModal.isOpen} onClose={goLiveModal.onClose} />
    </>
  );
};

export default PlanAndSubscription;
